import React from 'react';
import { FileText, FileSpreadsheet, File, Download } from 'lucide-react';

interface MediaRendererProps {
  message: {
    id: string;
    media_url?: string;
    media_name?: string;
    message_type: string;
  };
  isDeleted: boolean;
  onImageClick: (url: string) => void;
  onVideoClick: (url: string) => void;
  imageLoadingStates: { [messageId: string]: 'loading' | 'loaded' | 'error' };
  videoDurations: { [messageId: string]: string };
  onImageLoad: (messageId: string) => void;
  onImageError: (messageId: string) => void;
  onImageLoadStart: (messageId: string) => void;
  onVideoMetadata: (messageId: string, video: HTMLVideoElement) => void;
}

const MediaRenderer: React.FC<MediaRendererProps> = ({
  message,
  isDeleted,
  onImageClick,
  onVideoClick,
  imageLoadingStates,
  videoDurations,
  onImageLoad,
  onImageError,
  onImageLoadStart,
  onVideoMetadata
}) => {
  const mediaUrl = message.media_url || '';

  const getFileName = (message: { media_url?: string; media_name?: string }): string => {
    if (message.media_name && message.media_name.trim()) {
      return message.media_name.trim();
    }
    
    if (!message.media_url) {
      return 'Arquivo';
    }
    
    const parts = message.media_url.split('/');
    let fileName = parts[parts.length - 1];
    
    if (fileName.includes('?')) {
      fileName = fileName.split('?')[0];
    }
    
    if (fileName && fileName.includes('.')) {
      return fileName;
    }
    
    return 'Arquivo';
  };

  const getFileNameWithoutExtension = (fileName: string): string => {
    if (!fileName || fileName === 'Arquivo') {
      return fileName;
    }
    
    const lastDotIndex = fileName.lastIndexOf('.');
    if (lastDotIndex === -1) {
      return fileName;
    }
    
    return fileName.substring(0, lastDotIndex);
  };

  const getFileIcon = (messageType: string, fileName: string = ''): React.ReactNode => {
    const extension = fileName.split('.').pop()?.toLowerCase() || '';
    
    switch (extension) {
      case 'pdf':
        return <FileText size={20} color="#dc2626" />;
      case 'doc':
      case 'docx':
        return <FileText size={20} color="#2563eb" />;
      case 'xls':
      case 'xlsx':
      case 'csv':
        return <FileSpreadsheet size={20} color="#16a34a" />;
      case 'ppt':
      case 'pptx':
        return <File size={20} color="#ea580c" />;
      case 'txt':
        return <FileText size={20} color="#6b7280" />;
      default:
        return <File size={20} color="#6b7280" />;
    }
  };
  
  const getFileType = (fileName: string): string => {
    const extension = fileName.split('.').pop()?.toUpperCase() || '';
    return extension || 'DOCUMENTO';
  };

  if (!mediaUrl || isDeleted) {
    return null;
  }

  const fileName = getFileName(message);
  const fileIcon = getFileIcon(message.message_type, fileName);
  const messageType = message.message_type.toLowerCase();
  
  const fileExtension = fileName.split('.').pop()?.toLowerCase() || '';
  const isImageFile = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(fileExtension);
  const isAudioFile = ['mp3', 'wav', 'ogg', 'm4a', 'aac', 'flac'].includes(fileExtension);
  const isVideoFile = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'].includes(fileExtension);
  
  let finalType = messageType;
  if (isImageFile && messageType !== 'image') {
    finalType = 'image';
  } else if (isAudioFile && !['audio', 'ptt'].includes(messageType)) {
    finalType = 'audio';
  } else if (isVideoFile && messageType !== 'video') {
    finalType = 'video';
  }

  return (
    <div className="message-media">
      {(finalType === 'image' || isImageFile) && (
        <div className="media-image">
          {imageLoadingStates[message.id] === 'loading' && (
            <div className="image-loading">
              <div className="loading-spinner"></div>
              <span>Carregando imagem...</span>
            </div>
          )}
          
          {imageLoadingStates[message.id] === 'error' && (
            <div className="media-error">
              <span className="media-icon">🖼️</span>
              <span>Imagem não disponível</span>
              <button 
                className="retry-button"
                onClick={() => {
                  onImageLoadStart(message.id);
                  const img = document.querySelector(`#image-${message.id}`) as HTMLImageElement;
                  if (img) {
                    const currentSrc = img.src;
                    img.src = '';
                    setTimeout(() => {
                      img.src = currentSrc + '?retry=' + Date.now();
                    }, 100);
                  }
                }}
              >
                Tentar novamente
              </button>
            </div>
          )}
          
          <img 
            id={`image-${message.id}`}
            src={mediaUrl}
            alt="Imagem enviada"
            className={`message-image ${imageLoadingStates[message.id] === 'loaded' ? 'loaded' : ''}`}
            loading="lazy"
            decoding="async"
            style={{
              display: imageLoadingStates[message.id] === 'error' ? 'none' : 'block'
            }}
            onLoadStart={() => onImageLoadStart(message.id)}
            onLoad={() => onImageLoad(message.id)}
            onError={() => onImageError(message.id)}
            onClick={() => onImageClick(mediaUrl)}
            title="Clique para ampliar"
          />
        </div>
      )}

      {(finalType === 'audio' || finalType === 'ptt' || isAudioFile) && (
        <div className="media-audio">
          <audio controls preload="none">
            <source src={mediaUrl} />
            Seu navegador não suporta reprodução de áudio.
          </audio>
        </div>
      )}

      {(finalType === 'video' || isVideoFile) && (
        <div className="media-video">
          <div 
            className="video-preview modern-video"
            onClick={() => onVideoClick(mediaUrl)}
          >
            <video 
              className="video-thumbnail"
              preload="metadata"
              muted
              onLoadedMetadata={(e) => onVideoMetadata(message.id, e.target as HTMLVideoElement)}
            >
              <source src={mediaUrl} />
            </video>
            
            <div className="video-overlay-dark">
              <div className="video-play-minimal">
                <div className="play-triangle"></div>
              </div>
            </div>

            <div className="video-duration-badge">
              {videoDurations[message.id] || '0:00'}
            </div>
          </div>
        </div>
      )}

      {(!isImageFile && !isAudioFile && !isVideoFile && !['image', 'audio', 'video', 'ptt'].includes(finalType)) && (
        <div className="media-document-simple">
          <div className="document-card-simple">
            <div className="document-icon-box">
              {fileIcon}
            </div>
            <div className="document-details">
              <div className="document-filename" title={fileName}>
                {getFileNameWithoutExtension(fileName)}
              </div>
              <div className={`document-filetype document-filetype-${fileExtension}`}>
                {getFileType(fileName)}
              </div>
            </div>
            <a 
              href={mediaUrl}
              download={fileName}
              className="document-download-btn"
              onClick={(e) => e.stopPropagation()}
              title="Baixar arquivo"
            >
              <Download size={18} />
            </a>
          </div>
        </div>
      )}

      {finalType === 'sticker' && (
        <div className="media-sticker">
          <img 
            src={mediaUrl}
            alt="Sticker"
            className="message-sticker"
          />
        </div>
      )}
    </div>
  );
};

export default MediaRenderer;
